/**
 * ErrorBoundary for UnifiedParsingModal
 *
 * Ловит рантайм-ошибки в дереве модалки и показывает fallback UI вместо
 * полностью сломанной страницы. Логирует ошибку в консоль для дебага.
 */

import { Component, type ReactNode, type ErrorInfo } from 'react'
import { Result, Button, Space } from 'antd'
import { ReloadOutlined, CloseOutlined } from '@ant-design/icons'

interface ErrorBoundaryProps {
  children: ReactNode
  onReset?: () => void
  onClose?: () => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[UnifiedParsingModal ErrorBoundary]', error, info)
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null })
    this.props.onReset?.()
  }

  handleClose = () => {
    this.setState({ hasError: false, error: null })
    this.props.onClose?.()
  }

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || 'Неизвестная ошибка'

      return (
        <Result
          status="error"
          title="Что-то пошло не так"
          subTitle={`Произошла ошибка в модалке загрузки источников: ${errorMessage}`}
          extra={
            <Space>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={this.handleReload}
              >
                Перезагрузить
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={this.handleClose}
              >
                Закрыть
              </Button>
            </Space>
          }
        />
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
